import { useCallback } from 'react';
import {
  makeVar,
  ReactiveVar,
  useReactiveVar,
} from '@apollo/client';

import { appsPromotionBannerKey } from '@constants';
import { useStaticAppsBanner } from '@hook/useStaticAppsBanner';

export enum BannerPlacement {
  Main = 'Main',
  Category = 'Category',
  Profile = 'Profile',
}

const getIsBannerShownFromStorage = (): boolean | null => {
  try {
    return !!sessionStorage.getItem(appsPromotionBannerKey);
  } catch {
    return false;
  }
};

const setIsBannerShownToStorage = () => {
  try {
    sessionStorage.setItem(appsPromotionBannerKey, '1');
  } catch (err) {
    console.log(err);
  }
};

let openBannerPlacementVar: ReactiveVar<keyof typeof BannerPlacement | null> = (
  makeVar<BannerPlacement | null>(null)
);

if (typeof window !== 'undefined') {
  if (!window.OPEN_APPS_PROMOTION_BANNER_PLACEMENT) {
    window.OPEN_APPS_PROMOTION_BANNER_PLACEMENT = makeVar<BannerPlacement | null>(null);
  }

  openBannerPlacementVar = window.OPEN_APPS_PROMOTION_BANNER_PLACEMENT;
}

export const useAnimatedAppsBanner = (placement: BannerPlacement) => {
  const { isLoading, isCanShow } = useStaticAppsBanner();

  const openBannerPlacement = useReactiveVar(openBannerPlacementVar);

  const handleShow = useCallback(() => {
    try {
      openBannerPlacementVar(placement);

      setIsBannerShownToStorage();
    } catch (err) {
      console.error(err);
    }
  }, [placement]);

  const handleClose = useCallback(() => {
    try {
      openBannerPlacementVar(null);
    } catch (err) {
      console.error(err);
    }
  }, []);

  return {
    isLoading,
    isCanShow: isCanShow && !getIsBannerShownFromStorage(),
    placement,
    isVisible: openBannerPlacement === placement,
    handleShow,
    handleClose,
  };
};
